import React, { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { Checkbox } from "@mui/material";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Edit,
  Folder,
  MoreVertical,
  Search,
  User,
} from "lucide-react";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import TableComponent from "../../../shared/components/table";
import { ADD_USER, ARROW_LEFT } from "../../../assets";
import CaseFolderDetailsModal from "./case-folder/modals/CaseFolderDetailsModal";

type EntityType = "law_firm" | "institution";

type EntityItem = {
  id: number;
  type: EntityType;
  name: string;
  email: string;
  address: string;
  phone: string;
  totalCases: number;
  registrationStatus: "Approved" | "Pending" | "Rejected";
  teamSize: number;
  scnNumber?: string;
};

type CaseFolderRow = {
  id: number;
  title: string;
  appealNumber: string;
  filingDate: string;
  assignedCourt: string;
  courtNumber: string;
  status: string;
  sn?: number;
};

type TeamMemberRow = {
  id: number;
  name: string;
  email: string;
  phone: string;
  assignedCases: number;
  sn?: number;
};

const entitiesTabs = [
  { id: "law_firms", label: "Law firms" },
  { id: "institutions", label: "Institutions" },
] as const;

type EntitiesTabId = (typeof entitiesTabs)[number]["id"];

const allEntities: EntityItem[] = [
  {
    id: 1,
    type: "law_firm",
    name: "G & G Legal",
    email: "contact@gandglegal.com",
    address: "22 Kayode Street, Enugu",
    phone: "+234 807 654 2256",
    totalCases: 24,
    registrationStatus: "Approved",
    teamSize: 8,
    scnNumber: "839238CN",
  },
  {
    id: 2,
    type: "law_firm",
    name: "Ogunlaji & Co.",
    email: "info@ogunlajilegal.com",
    address: "12 Adeyemi Crescent, Lagos",
    phone: "+234 803 456 7821",
    totalCases: 18,
    registrationStatus: "Pending",
    teamSize: 5,
    scnNumber: "739128CN",
  },
  {
    id: 3,
    type: "institution",
    name: "Ministry of Justice",
    email: "moj@enugustate.gov.ng",
    address: "Government House, Enugu",
    phone: "+234 801 234 5678",
    totalCases: 35,
    registrationStatus: "Approved",
    teamSize: 12,
  },
  {
    id: 4,
    type: "institution",
    name: "Legal Aid Council",
    email: "support@legalaid.org.ng",
    address: "14 Justice Avenue, Abuja",
    phone: "+234 809 112 3344",
    totalCases: 9,
    registrationStatus: "Rejected",
    teamSize: 4,
  },
];

const caseFoldersMock: CaseFolderRow[] = [
  {
    id: 1,
    title: "Ogunlaji vs Adeyemi",
    appealNumber: "CV/3047/2018",
    filingDate: "31-05-2024",
    assignedCourt: "Hon. Justice Exe",
    courtNumber: "Court 3",
    status: "Ongoing",
  },
  {
    id: 2,
    title: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/1021/2022",
    filingDate: "15-04-2024",
    assignedCourt: "Hon. Justice Esther",
    courtNumber: "Court 1",
    status: "Awaiting assessment",
  },
];

const teamMembersMock: TeamMemberRow[] = [
  {
    id: 1,
    name: "Esther Ochendo",
    email: "esther.ochendo@gandglegal.com",
    phone: "+234 807 123 4567",
    assignedCases: 5,
  },
  {
    id: 2,
    name: "John Doe",
    email: "john.doe@gandglegal.com",
    phone: "+234 809 987 6543",
    assignedCases: 3,
  },
];

const columnHelper = createColumnHelper<EntityItem>();
const caseFolderColumnHelper = createColumnHelper<CaseFolderRow>();
const teamMemberColumnHelper = createColumnHelper<TeamMemberRow>();

const createCaseFolderColumns = (
  onOpenFolder: () => void,
  onShowDetails: () => void,
) => [
  caseFolderColumnHelper.accessor("sn", {
    header: "",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  caseFolderColumnHelper.accessor("title", {
    header: "Case title",
    cell: (info) => {
      const sn = info.row.original.sn;
      return (
        <span className="text-[#111827] text-sm">
          {sn}. {info.getValue()}
        </span>
      );
    },
  }),
  caseFolderColumnHelper.accessor("appealNumber", {
    header: "Appeal number",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  caseFolderColumnHelper.accessor("filingDate", {
    header: "Filing date",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  caseFolderColumnHelper.accessor("assignedCourt", {
    header: "Assigned court",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  caseFolderColumnHelper.accessor("courtNumber", {
    header: "Court number",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  caseFolderColumnHelper.accessor("status", {
    header: "Status",
    cell: (info) => (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#ECFDF3] text-[#027A48]">
        {info.getValue()}
      </span>
    ),
  }),
  caseFolderColumnHelper.display({
    id: "action",
    header: "Action",
    cell: () => (
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
          <MoreVertical className="w-5 h-5" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            anchor="bottom end"
            className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
          >
            <div className="px-1 py-1 flex flex-col gap-y-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={onOpenFolder}
                    className={`${
                      active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                  >
                    Open Folder
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={onShowDetails}
                    className={`${
                      active ? "bg-gray-50 text-[#111827]" : "text-[#111827]"
                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                  >
                    Folder details
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    ),
  }),
];

const createTeamMemberColumns = (
  onOpenFolder: () => void,
  onShowDetails: () => void,
) => [
  teamMemberColumnHelper.accessor("sn", {
    header: "",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  teamMemberColumnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  teamMemberColumnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  teamMemberColumnHelper.accessor("phone", {
    header: "Phone number",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  teamMemberColumnHelper.accessor("assignedCases", {
    header: "Assign cases",
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="text-[#111827] text-sm">
          {value} case{value === 1 ? "" : "s"}
        </span>
      );
    },
  }),
  teamMemberColumnHelper.display({
    id: "action",
    header: "Action",
    cell: () => (
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
          <MoreVertical className="w-5 h-5" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            anchor="bottom end"
            className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
          >
            <div className="px-1 py-1 flex flex-col gap-y-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={onOpenFolder}
                    className={`${
                      active ? "bg-[#E6F7F1] text-[#027A48]" : "text-[#344054]"
                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                  >
                    Open Folder
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={onShowDetails}
                    className={`${
                      active ? "bg-gray-50 text-[#111827]" : "text-[#111827]"
                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                  >
                    Folder details
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    ),
  }),
];

const Entities = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<EntitiesTabId>("law_firms");
  const [searchTerm, setSearchTerm] = useState("");
  const [registrationFilter, setRegistrationFilter] = useState<string>(
    "Registration status",
  );
  const [accountStatusFilter, setAccountStatusFilter] =
    useState<string>("Account status");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [caseSorting, setCaseSorting] = useState<SortingState>([]);
  const [teamSorting, setTeamSorting] = useState<SortingState>([]);
  const [teamSearch, setTeamSearch] = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState<string>("Status");
  const [teamEnabledFilter, setTeamEnabledFilter] = useState<string>("Enabled");

  const [selectedEntity, setSelectedEntity] = useState<EntityItem | null>(null);
  const [detailTab, setDetailTab] = useState<
    "case_folders" | "team_members" | "law_firm_info"
  >("case_folders");
  const [infoSubTab, setInfoSubTab] = useState<"firm" | "account_owner">(
    "firm",
  );
  const [showCaseFolderDetails, setShowCaseFolderDetails] = useState(false);

  const filteredEntities = useMemo(() => {
    let items = allEntities.filter((item) =>
      activeTab === "law_firms"
        ? item.type === "law_firm"
        : item.type === "institution",
    );

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(term) ||
          item.email.toLowerCase().includes(term) ||
          item.address.toLowerCase().includes(term),
      );
    }

    if (
      registrationFilter !== "Registration status" &&
      registrationFilter !== "All"
    ) {
      items = items.filter(
        (item) => item.registrationStatus === registrationFilter,
      );
    }

    if (accountStatusFilter === "Active") {
      items = items.filter((item) => item.registrationStatus === "Approved");
    } else if (accountStatusFilter === "Inactive") {
      items = items.filter((item) => item.registrationStatus !== "Approved");
    }

    return items.map((item, index) => ({
      ...item,
      sn: index + 1,
    })) as (EntityItem & { sn: number })[];
  }, [activeTab, searchTerm, registrationFilter, accountStatusFilter]);

  const teamMembersData = useMemo(
    () =>
      teamMembersMock
        .filter((member) => {
          if (!teamSearch.trim()) return true;
          const term = teamSearch.toLowerCase();
          return (
            member.name.toLowerCase().includes(term) ||
            member.email.toLowerCase().includes(term) ||
            member.phone.toLowerCase().includes(term)
          );
        })
        .map((member, index) => ({
          ...member,
          sn: index + 1,
        })),
    [teamSearch, teamStatusFilter, teamEnabledFilter],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor((row: any) => row.sn, {
          id: "sn",
          header: "SN",
          cell: ({ row }) => (
            <Checkbox
              color="success"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              sx={{
                color: "#E0E0E0",
                "&.Mui-checked": {
                  color: "#00B074",
                },
              }}
            />
          ),
        }),
        columnHelper.accessor("name", {
          header: "Name of Law Firm",
          cell: (info) => (
            <span className="text-[#101828] text-sm max-w-[220px] truncate block">
              {info.getValue()}
            </span>
          ),
        }),
        columnHelper.accessor("email", {
          header: "Email",
          cell: (info) => (
            <span className="text-[#667085] text-sm">{info.getValue()}</span>
          ),
        }),
        columnHelper.accessor("address", {
          header: "Address",
          cell: (info) => (
            <span className="text-[#667085] text-sm max-w-[260px] truncate block">
              {info.getValue()}
            </span>
          ),
        }),
        columnHelper.accessor("phone", {
          header: "Phone number",
          cell: (info) => (
            <span className="text-[#667085] text-sm">{info.getValue()}</span>
          ),
        }),
        columnHelper.accessor("totalCases", {
          header: "Total created cases",
          cell: (info) => (
            <span className="text-[#101828] text-sm">{info.getValue()}</span>
          ),
        }),
        columnHelper.accessor("registrationStatus", {
          header: "Registration Status",
          cell: (info) => {
            const value = info.getValue();
            const color =
              value === "Approved"
                ? "text-[#027A48] bg-[#ECFDF3]"
                : value === "Pending"
                  ? "text-[#B54708] bg-[#FFFAEB]"
                  : "text-[#B42318] bg-[#FEF3F2]";
            return (
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${color}`}
              >
                {value}
              </span>
            );
          },
        }),
        columnHelper.accessor("teamSize", {
          header: "Team",
          cell: (info) => (
            <span className="text-[#101828] text-sm">
              {info.getValue()} members
            </span>
          ),
        }),
        columnHelper.display({
          id: "action",
          header: "Action",
          cell: (info) => (
            <EntityActionMenu
              entity={info.row.original}
              onViewCaseFolders={(entity) => {
                setSelectedEntity(entity);
                setDetailTab("case_folders");
                setInfoSubTab("firm");
              }}
              onViewTeam={(entity) => {
                setSelectedEntity(entity);
                setDetailTab("team_members");
                setInfoSubTab("firm");
              }}
              onManageAccount={(entity) => {
                setSelectedEntity(entity);
                setDetailTab("law_firm_info");
                setInfoSubTab(
                  entity.type === "law_firm" ? "account_owner" : "firm",
                );
              }}
            />
          ),
        }),
      ] as any,
    [],
  );

  const handleOpenFolder = () => {
    navigate("/admin/case-folders/view", {
      state: {
        fromEntities: true,
        entityName: selectedEntity?.name,
        entityType: selectedEntity?.type,
        entityKind:
          selectedEntity?.type === "institution" ? "Organisation" : "Organisation",
      },
    });
  };

  const caseFolderColumns = useMemo(
    () =>
      createCaseFolderColumns(handleOpenFolder, () =>
        setShowCaseFolderDetails(true),
      ),
    [handleOpenFolder],
  );

  const teamMemberColumns = useMemo(
    () =>
      createTeamMemberColumns(handleOpenFolder, () =>
        setShowCaseFolderDetails(true),
      ),
    [handleOpenFolder],
  );

  const showListView = !selectedEntity;

  if (!showListView && selectedEntity) {
    const isLawFirm = selectedEntity.type === "law_firm";

    return (
      <div className="flex flex-col h-full min-h-screen">
        <div className="bg-white border-b border-[#E9EAEB]">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedEntity(null);
                  setDetailTab("case_folders");
                  setInfoSubTab("firm");
                }}
                className="h-9 w-9 rounded-full bg-[#F3F4F6] flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 text-[#111827]" />
              </button>
              <div className="flex items-center gap-x-2 text-sm text-[#667085]">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEntity(null);
                    setDetailTab("case_folders");
                    setInfoSubTab("firm");
                  }}
                  className="hover:text-[#111827]"
                >
                  Entities
                </button>

                <img src={ARROW_LEFT} className="w-[19px] h-[19px]" alt="" />

                <span className="text-[#667085]">
                  {isLawFirm ? "Law firms" : "Institutions"}
                </span>
                <img src={ARROW_LEFT} className="w-[19px] h-[19px]" alt="" />
                <span className="font-medium text-[#111827]">
                  {selectedEntity.name}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-5 py-2 rounded-lg bg-[#00B074] text-white text-sm font-medium"
              >
                Approve registration
              </button>
              <button
                type="button"
                className="px-5 py-2 rounded-lg bg-[#F04438] text-white text-sm font-medium"
              >
                Suspend account
              </button>
            </div>
          </div>

          <div className="px-8 flex pt-5 items-center gap-6 text-sm font-medium border-t border-[#E9EAEB] mt-1">
            <button
              type="button"
              onClick={() => setDetailTab("case_folders")}
              className={`pb-3 border-b-2 ${
                detailTab === "case_folders"
                  ? "border-[#111827] text-[#111827]"
                  : "border-transparent text-[#667085]"
              }`}
            >
              Case folders
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("team_members")}
              className={`pb-3 border-b-2 ${
                detailTab === "team_members"
                  ? "border-[#111827] text-[#111827]"
                  : "border-transparent text-[#667085]"
              }`}
            >
              Team members
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("law_firm_info")}
              className={`pb-3 border-b-2 ${
                detailTab === "law_firm_info"
                  ? "border-[#111827] text-[#111827]"
                  : "border-transparent text-[#667085]"
              }`}
            >
              {isLawFirm ? "Law Firm information" : "Institution information"}
            </button>
          </div>
        </div>

        <div className="flex-1 px-8 py-6 pb-10 flex flex-col gap-6">
          {detailTab === "case_folders" && (
            <TableComponent
                DATA={caseFoldersMock.map((item, index) => ({
                  ...item,
                  sn: index + 1,
                }))}
                COLUMNS={caseFolderColumns as any}
                sorting={caseSorting}
                setSorting={setCaseSorting}
              />
          )}

          {detailTab === "team_members" && (
            <div>
              <div className="pt-4 pb-2  flex items-center gap-4">
                <div className="relative w-full max-w-[320px]">
                  <input
                    type="text"
                    placeholder="Search"
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#98A2B3]"
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#98A2B3]" />
                </div>

                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
                    <span>{teamStatusFilter}</span>
                    <ChevronDown className="w-4 h-4 text-[#667085]" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute left-0 mt-2 w-44 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                      <div className="px-1 py-1 flex flex-col gap-y-1">
                        {["Status", "All", "Active", "Inactive"].map(
                          (option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => setTeamStatusFilter(option)}
                                  className={`${
                                    active
                                      ? "bg-gray-50 text-[#111827]"
                                      : "text-[#111827]"
                                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ),
                        )}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
                    <span>{teamEnabledFilter}</span>
                    <ChevronDown className="w-4 h-4 text-[#667085]" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute left-0 mt-2 w-44 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                      <div className="px-1 py-1 flex flex-col gap-y-1">
                        {["Enabled", "Enabled", "Disabled"].map((option) => (
                          <Menu.Item key={option}>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => setTeamEnabledFilter(option)}
                                className={`${
                                  active
                                    ? "bg-gray-50 text-[#111827]"
                                    : "text-[#111827]"
                                } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                              >
                                {option}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
              <TableComponent
                DATA={teamMembersData}
                COLUMNS={teamMemberColumns as any}
                sorting={teamSorting}
                setSorting={setTeamSorting}
              />
            </div>
          )}

          {detailTab === "law_firm_info" && (
            <>
              {isLawFirm && (
                <div className="flex items-center gap-6 text-sm font-medium">
                  <button
                    type="button"
                    onClick={() => setInfoSubTab("firm")}
                    className={`pb-3 border-b-2 ${
                      infoSubTab === "firm"
                        ? "border-[#111827] text-[#111827]"
                        : "border-transparent text-[#667085]"
                    }`}
                  >
                    Firm&apos;s Information
                  </button>
                  <button
                    type="button"
                    onClick={() => setInfoSubTab("account_owner")}
                    className={`pb-3 border-b-2 ${
                      infoSubTab === "account_owner"
                        ? "border-[#111827] text-[#111827]"
                        : "border-transparent text-[#667085]"
                    }`}
                  >
                    Account owner’s information
                  </button>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                <div className="px-6 py-4 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <span className="text-sm font-medium text-[#101828]">
                    {infoSubTab === "account_owner"
                      ? "Personal Information"
                      : "Firms Information"}
                  </span>
                </div>
                <div className="px-6 py-6 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
                  {(!isLawFirm || infoSubTab === "firm") && (
                    <>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Name of Law firm
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.name}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Firms Email
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.email}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Contact Number
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.phone}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Firm Address
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.address}
                        </div>
                      </div>
                    </>
                  )}

                  {isLawFirm && infoSubTab === "account_owner" && (
                    <>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Name
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.name}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Email address
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.email}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          SCN Number
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.scnNumber || "839238CN"}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Phone number
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.phone}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-[#667085]">
                          Whatsapp number
                        </span>
                        <div className="border border-[#E5E7EB] rounded-lg bg-white px-3 py-2.5 text-sm text-[#101828]">
                          {selectedEntity.phone}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <CaseFolderDetailsModal
          open={showCaseFolderDetails}
          close={() => setShowCaseFolderDetails(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b flex items-center justify-between w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">Entities</h1>
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827] shadow-sm hover:bg-gray-50"
          >
            <img src={ADD_USER} alt="" className="w-4 h-4" />
            <span>Add entity</span>
          </button>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <Link
              to="/admin/overview"
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </Link>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">Entities</span>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-[#E9EAEB] px-8 pt-4">
        <div className="overflow-x-auto">
          <div className="flex items-center gap-6 text-sm font-medium whitespace-nowrap w-max">
            {entitiesTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-[#111827] text-[#111827]"
                    : "border-transparent text-[#667085]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="relative w-full max-w-[320px]">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#98A2B3]"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#98A2B3]" />
          </div>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
              <span>{registrationFilter}</span>
              <ChevronDown className="w-4 h-4 text-[#667085]" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 mt-2 w-48 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                <div className="px-1 py-1 flex flex-col gap-y-1">
                  {[
                    "Registration status",
                    "All",
                    "Approved",
                    "Pending",
                    "Rejected",
                  ].map((option) => (
                    <Menu.Item key={option}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => setRegistrationFilter(option)}
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                        >
                          {option}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
              <span>{accountStatusFilter}</span>
              <ChevronDown className="w-4 h-4 text-[#667085]" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 mt-2 w-44 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                <div className="px-1 py-1 flex flex-col gap-y-1">
                  {["Account status", "All", "Active", "Inactive"].map(
                    (option) => (
                      <Menu.Item key={option}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setAccountStatusFilter(option)}
                            className={`${
                              active
                                ? "bg-gray-50 text-[#111827]"
                                : "text-[#111827]"
                            } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                          >
                            {option}
                          </button>
                        )}
                      </Menu.Item>
                    ),
                  )}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      <div className="flex-1 px-8 pb-10">
        <div className="bg-white rounded-2xl">
          <TableComponent
            DATA={filteredEntities}
            COLUMNS={columns as any}
            sorting={sorting}
            setSorting={setSorting}
          />
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E4E7EC] text-sm text-[#667085]">
            <div className="flex items-center gap-2">
              <span>
                Page <span className="font-medium">1</span> of{" "}
                <span className="font-medium">1</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-lg border border-[#D0D5DD] bg-white hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1.5 rounded-lg border border-[#D0D5DD] bg-white hover:bg-gray-50">
                  Next
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span>Entries per page:</span>
                <select className="border border-[#D0D5DD] rounded-lg px-2 py-1 bg-white focus:outline-none text-sm">
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type EntityActionMenuProps = {
  entity: EntityItem;
  onViewCaseFolders: (entity: EntityItem) => void;
  onViewTeam: (entity: EntityItem) => void;
  onManageAccount: (entity: EntityItem) => void;
};

const EntityActionMenu = ({
  entity,
  onViewCaseFolders,
  onViewTeam,
  onManageAccount,
}: EntityActionMenuProps) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
        <MoreVertical className="w-5 h-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          className="absolute right-0 mt-2 w-60 origin-top-right rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
        >
          <div className="px-1 py-1 flex flex-col gap-y-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onViewCaseFolders(entity)}
                  className={`${
                    active ? "bg-[#D1FADF] text-[#111827]" : "text-[#111827]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  <span className="flex items-center gap-x-3">
                    <Folder className="w-4 h-4" />
                    <span>View case folders</span>
                  </span>
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onViewTeam(entity)}
                  className={`${
                    active ? "bg-[#F9FAFB] text-[#111827]" : "text-[#111827]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  <span className="flex items-center gap-x-3">
                    <User className="w-4 h-4" />
                    <span>View team</span>
                  </span>
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onManageAccount(entity)}
                  className={`${
                    active ? "bg-[#F9FAFB] text-[#111827]" : "text-[#111827]"
                  } group flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-sm font-medium`}
                >
                  <span className="flex items-center gap-x-3">
                    <Edit className="w-4 h-4" />
                    <span>Manage account</span>
                  </span>
                  <Check className="w-4 h-4" />
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default Entities;
